import { RiskDirectoryNodeView, StandardNode } from "api/generated/model";
import { ProcessNode, RiskNode } from "org/risks/types";

import { DirectoryNode } from "../types";

/**
 * Based on 2 root nodes, generate a third that have all the risks and entities of both.
 *
 * Warning: This merged node does not guarantee path + name uniqueness.
 *
 * Intended usage: Create a root node with all the risks and entities from the same risk directory viewed at 2 points in time.
 *
 * Notes:
 * - Common properties comes from rootNode1.
 * - All assessments are removed to avoid confusion.
 */
export function mergeRootNodes(
    rootNode1: RiskDirectoryNodeView,
    rootNode2: RiskDirectoryNodeView
): RiskDirectoryNodeView {
    validateNodesCompatible(rootNode1, rootNode2);

    return {
        ...rootNode1,
        content: {
            ...rootNode1.content,
            scope: {
                entityIds: mergeEntityIds(
                    rootNode1.content.scope.entityIds,
                    rootNode2.content.scope.entityIds
                ),
            },
            metadataValues: {},
        },
        nodes: mergeChildNodes(rootNode1.nodes, rootNode2.nodes),
    };
}

function mergeNodes(node1: StandardNode, node2: StandardNode): StandardNode {
    validateNodesCompatible(node1, node2);

    if (node1.type === "Process" && node2.type === "Process") {
        return mergeProcessNodes(node1, node2);
    }
    if (node1.type === "Risk" && node2.type === "Risk") {
        return mergeRiskNodes(node1, node2);
    }

    throw new Error(
        `Incompatible node types ${node1.type} and ${node2.type} for node with id ${node1.id}`
    );
}

function mergeProcessNodes(
    node1: StandardNode,
    node2: StandardNode
): StandardNode {
    // Type narrowing check
    if (node1.type !== "Process" || node2.type !== "Process") {
        throw new Error(
            `Expected Process nodes but got ${node1.type} and ${node2.type}`
        );
    }

    const mergedNodes = mergeChildNodes(node1.nodes, node2.nodes);
    const scope1 = node1.content.scope;
    const scope2 = node2.content.scope;

    if (scope1.type === "Global" && scope2.type === "Global") {
        return buildProcessNode(node1, mergedNodes, {
            type: "Global",
            entityIds: mergeEntityIds(scope1.entityIds, scope2.entityIds),
        });
    }

    if (scope1.type === "Local" && scope2.type === "Local") {
        validateLocalEntityIdMatch(scope1.entityId, scope2.entityId, node1);
        return buildProcessNode(node1, mergedNodes, scope1);
    }

    throw new Error(
        `Incompatible node scopes ${scope1.type} and ${scope2.type} for Process node with id ${node1.id}`
    );
}

function mergeRiskNodes(
    node1: StandardNode,
    node2: StandardNode
): StandardNode {
    // Type narrowing check
    if (node1.type !== "Risk" || node2.type !== "Risk") {
        throw new Error(
            `Expected Risk nodes but got ${node1.type} and ${node2.type}`
        );
    }

    const scope1 = node1.content.scope;
    const scope2 = node2.content.scope;

    if (
        scope1.type === "GlobalWithContent" &&
        scope2.type === "GlobalWithContent"
    ) {
        return buildRiskNode(node1, {
            type: "GlobalWithContent",
            entityIds: mergeEntityIds(scope1.entityIds, scope2.entityIds),
            entityIdContent: {},
        });
    }

    if (
        scope1.type === "LocalWithContent" &&
        scope2.type === "LocalWithContent"
    ) {
        validateLocalEntityIdMatch(scope1.entityId, scope2.entityId, node1);
        return buildRiskNode(node1, {
            type: "LocalWithContent",
            entityId: scope1.entityId,
            content: {
                metadataValues: {},
                mitigatingControls: [],
                assessmentStatus: { type: "NeverAssessed" },
            },
        });
    }

    throw new Error(
        `Incompatible node scopes ${scope1.type} and ${scope2.type} for Risk node with id ${node1.id}`
    );
}

function mergeEntityIds(entityIds1: number[], entityIds2: number[]): number[] {
    return [...new Set([...entityIds1, ...entityIds2])];
}

function mergeChildNodes(
    nodes1: StandardNode[],
    nodes2: StandardNode[]
): StandardNode[] {
    const mergedChildMap = new Map(nodes1.map((node) => [node.id, node]));

    for (const child2 of nodes2) {
        const child1 = mergedChildMap.get(child2.id);
        if (child1 !== undefined) {
            mergedChildMap.set(child1.id, mergeNodes(child1, child2));
        } else {
            mergedChildMap.set(child2.id, child2);
        }
    }

    return [...mergedChildMap.values()];
}

// Validators

function validateNodesCompatible(
    node1: DirectoryNode,
    node2: DirectoryNode
): void {
    if (node1.id !== node2.id) {
        throw new Error(
            `Cannot merge nodes with different ids: ${node1.id} and ${node2.id}`
        );
    }
    if (node1.type !== node2.type) {
        throw new Error(`Incompatible node types for node with id ${node1.id}`);
    }
    if (
        node1.type !== "RiskDirectory" &&
        node2.type !== "RiskDirectory" &&
        node1.content.scope.type !== node2.content.scope.type
    ) {
        throw new Error(
            `Incompatible node scopes ${node1.content.scope.type} and ${node2.content.scope.type} for node with id ${node1.id}`
        );
    }
}

function validateLocalEntityIdMatch(
    entityId1: number,
    entityId2: number,
    node: StandardNode
): void {
    if (entityId1 !== entityId2) {
        throw new Error(
            `Incompatible Local scopes with different entityIds ${entityId1} and ${entityId2} for ${node.type} node with id ${node.id}`
        );
    }
}

// Build helper functions

function buildProcessNode(
    baseNode: ProcessNode,
    nodes: StandardNode[],
    scope: ProcessNode["content"]["scope"]
): StandardNode {
    return {
        ...baseNode,
        type: "Process",
        content: {
            ...baseNode.content,
            scope,
            metadataValues: {},
        },
        nodes,
    };
}

function buildRiskNode(
    baseNode: RiskNode,
    scope: RiskNode["content"]["scope"]
): StandardNode {
    return {
        ...baseNode,
        type: "Risk",
        content: {
            ...baseNode.content,
            scope,
            metadataValues: {},
        },
    };
}
